import { FABRIC_HEADERS } from '@/utilities/constants/metadata';
import config from '@/config';
import { ErrorType } from '@/utilities/errors/types';
import { APIServiceType } from '../types';
import {
  setRecentlyPlayedError,
  setRecentlyPlayedStatus,
} from '@/store/actions';
import { RecentlyPlayedItem } from '@/store/slices/recentlyPlayed/recentlyPlayed';
import { CustomError } from '@/utilities/errors/CustomError';
import handleRecentlyPlayedData from './helpers/handle-recently-played-data';
import { useStore } from '@/store/useStore';
import {
  buildFabricAuthHeaders,
  createApiAxios,
  getFabricAuthTokens,
} from '@/utilities/axios/api-axios';
import { logAndThrowApiError } from '@/api/helpers/log-and-throw-api-error';

const RECENTLY_GAMES_OPERATION = {
  GET: 'read',
  ADD: 'add',
} as const;

export interface RecentlyPlayedOptions extends Record<string, unknown> {
  includeGameDetails?: boolean;
}

export interface AddRecentlyPlayedPayload {
  gameId: string;
}

/**
 * Fetches recently played games from the API
 * Pure API function - handles only the request/response
 */
export const fetchRecentlyPlayedApi = async (
  options: RecentlyPlayedOptions = {},
): Promise<RecentlyPlayedItem[]> => {
  const endpoint = config?.fabric?.endpoints?.userActivity?.recentlyPlayedGames;
  const { baseURL, region, brand, platform, product } =
    useStore.getState().runtime;

  if (!endpoint || !product || !platform || !region) {
    throw new CustomError(
      '[GW][FETCH_RECENTLY_PLAYED] Missing required parameters',
      {
        type: ErrorType.API,
        reason: 'Missing endpoint, product, platform, or region',
        service: APIServiceType.RECENTLY_PLAYED,
      },
    );
  }

  const operation = RECENTLY_GAMES_OPERATION.GET;
  const includeGameDetails = options.includeGameDetails ?? true;
  const apiUrl = `${endpoint}/${operation}?includeGameDetails=${includeGameDetails}&product=${product}&gameIdentifierType=id&platform=${platform === 'desktop' ? 'desktop' : 'mobile'}`;

  setRecentlyPlayedStatus({ loading: true });

  try {
    const tokens = await getFabricAuthTokens('FETCH_RECENTLY_PLAYED');

    const instance = createApiAxios();
    const response = await instance.get<RecentlyPlayedItem[]>(apiUrl, {
      baseURL,
      headers: {
        ...buildFabricAuthHeaders(tokens),
        [FABRIC_HEADERS.X_BRAND]: brand,
        [FABRIC_HEADERS.X_REGION_CODE]: region,
      },
    });

    return response?.data || [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.name : 'Unknown error';

    setRecentlyPlayedStatus({ loading: false });
    setRecentlyPlayedError({
      type: errorMessage,
      details: {
        message: errorMessage,
        reason: null,
      },
    });

    throw new CustomError(
      '[GW][FETCH_RECENTLY_PLAYED] Error fetching recently played games',
      {
        type: ErrorType.API,
        reason: errorMessage,
        service: APIServiceType.RECENTLY_PLAYED,
        stack: error instanceof Error ? error.stack : undefined,
        context: {
          error: errorMessage,
        },
      },
    );
  }
};

/**
 * Imperative fetch (e.g. tests). Prefer {@link useRecentlyPlayed} in UI.
 * Caching / retries: handled by TanStack Query when using the hook.
 */
export const fetchRecentlyPlayed = async (
  options: RecentlyPlayedOptions = {},
): Promise<RecentlyPlayedItem[] | undefined> => {
  try {
    const data = await fetchRecentlyPlayedApi(options);
    handleRecentlyPlayedData(data);
    return data;
  } catch {
    return undefined;
  }
};

/**
 * Adds a game to recently played via API
 */
export const addRecentlyPlayedGameApi = async ({
  gameId,
}: AddRecentlyPlayedPayload): Promise<void> => {
  const endpoint = config?.fabric?.endpoints?.userActivity?.recentlyPlayedGames;
  const { baseURL, region, brand, platform, product } =
    useStore.getState().runtime;

  if (!endpoint || !product || !platform || !region || !gameId) {
    throw new CustomError(
      '[GW][ADD_RECENTLY_PLAYED] Missing required parameters',
      {
        type: ErrorType.API,
        reason: 'Missing endpoint, product, platform, region, or gameId',
        service: APIServiceType.RECENTLY_PLAYED,
      },
    );
  }

  const operation = RECENTLY_GAMES_OPERATION.ADD;
  const apiUrl = `${endpoint}/${operation}?gameCode=${gameId}&product=${product}&gameIdentifierType=id&platform=${platform === 'desktop' ? 'desktop' : 'mobile'}`;

  try {
    const tokens = await getFabricAuthTokens('ADD_RECENTLY_PLAYED');

    const instance = createApiAxios();
    await instance.get(apiUrl, {
      baseURL,
      headers: {
        ...buildFabricAuthHeaders(tokens),
        [FABRIC_HEADERS.X_BRAND]: brand,
        [FABRIC_HEADERS.X_REGION_CODE]: region,
      },
    });
  } catch (error) {
    logAndThrowApiError({
      error,
      logName:
        error instanceof Error && error.message.includes('[GW]')
          ? error.message
          : '[GW][ADD_RECENTLY_PLAYED] Failed',
      description: 'Failed to add recently played game',
      service: APIServiceType.RECENTLY_PLAYED,
      customErrorMessage: '[RECENTLY_PLAYED] Error adding recently played',
      context: {
        gameId,
      },
    });
  }
};

export const addRecentlyPlayedGame = async (
  payload: AddRecentlyPlayedPayload,
): Promise<void> => addRecentlyPlayedGameApi(payload);

export default fetchRecentlyPlayed;
